import React, { useState, useCallback, useEffect } from 'react';
import { FloatingPanel, PanelSection } from '../ui/Panel';
import { Button } from '../ui/Button';
import { formatMeasurement } from '../../utils/measurements';

/**
 * PDFMeasurementToolbar - Toolbar for PDF measurement tools
 */
export const PDFMeasurementToolbar = ({
  activeTool,
  onToolChange,
  onClearAll,
  measurementCount,
  isMobile = false,
}) => {
  const tools = [
    { id: 'linear', icon: '📏', label: 'Linear', title: 'Measure distance between two points' },
    { id: 'area', icon: '⬡', label: 'Area', title: 'Measure area of a polygon' },
  ];

  return (
    <div
      style={{
        display: 'flex',
        gap: '4px',
        padding: '4px',
        background: 'rgba(12,18,24,0.95)',
        borderRadius: '6px',
        border: '1px solid rgba(0,200,255,0.3)',
      }}
    >
      {tools.map((tool) => (
        <button
          key={tool.id}
          onClick={() => onToolChange(activeTool === tool.id ? null : tool.id)}
          title={tool.title}
          style={{
            background: activeTool === tool.id ? 'rgba(0,200,255,0.3)' : 'transparent',
            border: activeTool === tool.id ? '1px solid #00c8ff' : '1px solid transparent',
            borderRadius: '4px',
            padding: isMobile ? '8px 12px' : '6px 10px',
            color: activeTool === tool.id ? '#00c8ff' : '#a0b0c0',
            fontSize: isMobile ? '14px' : '12px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            transition: 'all 0.15s',
          }}
        >
          <span>{tool.icon}</span>
          {!isMobile && <span>{tool.label}</span>}
        </button>
      ))}
      {measurementCount > 0 && (
        <button
          onClick={onClearAll}
          title="Clear all measurements"
          style={{
            background: 'transparent',
            border: '1px solid rgba(255,100,100,0.3)',
            borderRadius: '4px',
            padding: isMobile ? '8px 12px' : '6px 10px',
            color: '#ff6666',
            fontSize: isMobile ? '14px' : '12px',
            cursor: 'pointer',
            marginLeft: '4px',
          }}
        >
          🗑️
        </button>
      )}
    </div>
  );
};

/**
 * PDFMeasurementTotals - Panel showing measurement totals
 */
export const PDFMeasurementTotals = ({
  measurements,
  selectedIds,
  units = 'imperial',
  onClose,
  isMobile = false,
}) => {
  // Calculate totals
  const linearMeasurements = measurements.filter(m => m.type === 'linear');
  const areaMeasurements = measurements.filter(m => m.type === 'area');

  const selectedAreas = areaMeasurements.filter(m => selectedIds.includes(m.id));
  const totalSelectedArea = selectedAreas.reduce((sum, m) => sum + (m.area || 0), 0);
  const totalAllArea = areaMeasurements.reduce((sum, m) => sum + (m.area || 0), 0);

  const totalLinear = linearMeasurements.reduce((sum, m) => sum + (m.length || 0), 0);

  // Format area in sq ft
  const formatArea = (sqInches) => {
    const sqFeet = sqInches / 144; // 144 sq inches per sq foot
    return `${sqFeet.toLocaleString(undefined, { maximumFractionDigits: 1 })} sq ft`;
  };

  // Format linear measurement (convert inches to feet for formatMeasurement)
  const formatLinear = (inches) => {
    return formatMeasurement(inches / 12, units);
  };

  if (measurements.length === 0) return null;

  return (
    <FloatingPanel
      title="Measurements"
      onClose={onClose}
      x={isMobile ? 10 : undefined}
      y={isMobile ? 'auto' : 60}
      width={isMobile ? 180 : 200}
      style={isMobile ? { bottom: 70, top: 'auto' } : { right: 10, left: 'auto' }}
      isMobile={isMobile}
    >
      <div style={{ padding: '10px' }}>
        {linearMeasurements.length > 0 && (
          <PanelSection title={`Linear (${linearMeasurements.length})`}>
            <div style={{
              color: '#00c8ff',
              fontSize: '14px',
              fontWeight: 'bold',
              marginBottom: '4px',
            }}>
              {formatLinear(totalLinear)}
            </div>
            <div style={{ color: '#6080a0', fontSize: '10px' }}>
              Total of all linear measurements
            </div>
          </PanelSection>
        )}

        {areaMeasurements.length > 0 && (
          <PanelSection title={`Areas (${areaMeasurements.length})`}>
            <div style={{
              color: '#00ffaa',
              fontSize: '14px',
              fontWeight: 'bold',
              marginBottom: '4px',
            }}>
              {formatArea(totalAllArea)}
            </div>
            <div style={{ color: '#6080a0', fontSize: '10px', marginBottom: '8px' }}>
              Total of all areas
            </div>

            {selectedAreas.length > 0 && selectedAreas.length < areaMeasurements.length && (
              <>
                <div style={{
                  color: '#ffaa00',
                  fontSize: '13px',
                  fontWeight: 'bold',
                  marginTop: '8px',
                }}>
                  {formatArea(totalSelectedArea)}
                </div>
                <div style={{ color: '#6080a0', fontSize: '10px' }}>
                  Selected ({selectedAreas.length})
                </div>
              </>
            )}
          </PanelSection>
        )}
      </div>
    </FloatingPanel>
  );
};

/**
 * Calculate polygon area using the Shoelace formula
 */
export const calculatePolygonArea = (points) => {
  if (points.length < 3) return 0;

  let area = 0;
  const n = points.length;

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += points[i].x * points[j].y;
    area -= points[j].x * points[i].y;
  }

  return Math.abs(area / 2);
};

/**
 * Calculate distance between two points
 */
export const calculateDistance = (p1, p2) => {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Convert floor plan pixels to real-world inches based on PDF scale
 * Floor plan: 40px = 1 foot = 12 inches
 */
export const pixelsToInches = (pixels) => {
  const FLOOR_PLAN_PX_PER_INCH = 40 / 12;
  return pixels / FLOOR_PLAN_PX_PER_INCH;
};

/**
 * Convert floor plan square pixels to real-world square inches
 */
export const squarePixelsToSquareInches = (sqPixels) => {
  const FLOOR_PLAN_PX_PER_INCH = 40 / 12;
  return sqPixels / (FLOOR_PLAN_PX_PER_INCH * FLOOR_PLAN_PX_PER_INCH);
};

/**
 * Hook for managing PDF measurements
 */
export const usePDFMeasurements = (initialMeasurements = []) => {
  const [measurements, setMeasurements] = useState(initialMeasurements);
  const [selectedMeasurementIds, setSelectedMeasurementIds] = useState([]);
  const [activeMeasureTool, setActiveMeasureTool] = useState(null);
  const [drawingMeasurement, setDrawingMeasurement] = useState(null);

  // Add a new measurement
  const addMeasurement = useCallback((measurement) => {
    const id = `measure-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newMeasurement = { ...measurement, id };
    setMeasurements(prev => [...prev, newMeasurement]);
    return id;
  }, []);

  // Remove a measurement
  const removeMeasurement = useCallback((id) => {
    setMeasurements(prev => prev.filter(m => m.id !== id));
    setSelectedMeasurementIds(prev => prev.filter(sid => sid !== id));
  }, []);

  // Clear all measurements
  const clearMeasurements = useCallback(() => {
    setMeasurements([]);
    setSelectedMeasurementIds([]);
  }, []);

  // Toggle measurement selection
  const toggleMeasurementSelection = useCallback((id, multiSelect = false) => {
    setSelectedMeasurementIds(prev => {
      if (multiSelect) {
        return prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id];
      }
      return prev.includes(id) && prev.length === 1 ? [] : [id];
    });
  }, []);

  // Clear selection
  const clearMeasurementSelection = useCallback(() => {
    setSelectedMeasurementIds([]);
  }, []);

  // Start drawing a measurement
  const startMeasurement = useCallback((point, type) => {
    setDrawingMeasurement({
      type,
      points: [point],
      startTime: Date.now(),
    });
  }, []);

  // Add point to current measurement
  const addMeasurementPoint = useCallback((point) => {
    setDrawingMeasurement(prev => {
      if (!prev) return null;
      return {
        ...prev,
        points: [...prev.points, point],
      };
    });
  }, []);

  // Update last point (for preview)
  const updateMeasurementPreview = useCallback((point) => {
    setDrawingMeasurement(prev => {
      if (!prev || prev.points.length === 0) return prev;
      const points = [...prev.points];
      if (prev.type === 'linear' && points.length === 2) {
        points[1] = point;
      } else if (prev.type === 'area') {
        // For area, add preview point at end
        if (points.length > 0) {
          return { ...prev, previewPoint: point };
        }
      }
      return { ...prev, points, previewPoint: point };
    });
  }, []);

  // Complete the current measurement
  const completeMeasurement = useCallback(() => {
    if (!drawingMeasurement) return null;

    const { type, points } = drawingMeasurement;

    if (type === 'linear' && points.length >= 2) {
      const length = pixelsToInches(calculateDistance(points[0], points[1]));
      const measurement = {
        type: 'linear',
        points: [points[0], points[1]],
        length,
      };
      const id = addMeasurement(measurement);
      setDrawingMeasurement(null);
      return id;
    }

    if (type === 'area' && points.length >= 3) {
      const areaPixels = calculatePolygonArea(points);
      const area = squarePixelsToSquareInches(areaPixels);
      const measurement = {
        type: 'area',
        points,
        area,
      };
      const id = addMeasurement(measurement);
      setDrawingMeasurement(null);
      return id;
    }

    return null;
  }, [drawingMeasurement, addMeasurement]);

  // Cancel current measurement
  const cancelMeasurement = useCallback(() => {
    setDrawingMeasurement(null);
  }, []);

  return {
    measurements,
    setMeasurements,
    selectedMeasurementIds,
    activeMeasureTool,
    setActiveMeasureTool,
    drawingMeasurement,
    addMeasurement,
    removeMeasurement,
    clearMeasurements,
    toggleMeasurementSelection,
    clearMeasurementSelection,
    startMeasurement,
    addMeasurementPoint,
    updateMeasurementPreview,
    completeMeasurement,
    cancelMeasurement,
  };
};

export default PDFMeasurementToolbar;
